import {
  ContentBlock,
  ContentState,
  Editor,
  EditorState,
  RawDraftContentBlock,
  RawDraftEntity,
} from "draft-js";
import { useCallback, useEffect, useRef, useState } from "react";

import CtaButtonTemplate from "@/components/editors/template-blocks/cta-button/CtaButtonTemplate";
import { ServiceFormTemplate } from "@/components/editors/template-blocks/service-form/ServiceFormTemplate";
import { ServiceMenuCalendarTemplate } from "@/components/editors/template-blocks/service-menu/ServiceMenuCalendarTemplate";
import { ServiceMenuTemplate } from "@/components/editors/template-blocks/service-menu/ServiceMenuTemplate";
import DraftStyles from "@/models/editors/draft-js-rte/draftjs.style";
import {
  customStyleFn,
  customStyleMap,
} from "@/models/editors/draft-js-rte/renderConfig";
import { fontFamilyOptions } from "@/models/editors/editor";
import { CtaButtonTypeTemplate } from "@/states/page-editor/template-parts/cta-button";
import { FormServiceTemplate } from "@/states/page-editor/template-parts/service-form";
import { ServiceMenuTemplate as IServiceMenuTemplate } from "@/states/page-editor/template-parts/service-menu";
import { TemplatePart } from "@/states/page-editor/template-parts/types";
import { useServiceInfo } from "@/states/service-form/selectors";
import { ServiceViewType } from "@/apis/clients";
import { HeadingTypeTemplate } from "@/states/page-editor/template-parts/heading";
import ServiceHeadingTemplate from "./template-blocks/heading/ServiceHeadingTemplate";
/**
 * ページに紐づくサービスとテンプレートのBlockKey
 */
export interface ConnectedService {
  serviceId: number;
  templateBlockKey: string;
}

// part.contentがContentStateになっていないので応急処置としてinterfaceとユーザ定義TypeGuardを利用
// TODO Page APIのサーバ繋ぎ込み時に再度確認し、この部分を不要にしたい
type contentType =
  | {
      entityMap: { [key: string]: RawDraftEntity };
      blockMap: { [key: string]: RawDraftContentBlock };
      hasText: undefined;
    }
  | ContentState;

interface TemplatePartWithContent {
  content: contentType | ContentState;
  table: Array<{
    content: contentType;
  }>;
  titleContent: {
    content: contentType;
  };
  buttonContent: {
    content: contentType;
  };
}

export const TEMPLATE_PART_BLOCK_KEY = "TEMPLATE";

interface CustomBlockProps {
  block: ContentBlock;
  blockProps: {
    part: TemplatePart & TemplatePartWithContent;
    options: EditorDisplayOptions;
  };
}

const CustomBlockComponent = ({ block, blockProps }: CustomBlockProps) => {
  const key = block.getKey();
  const { part, options } = blockProps;

  return (
    <div style={{ fontFamily: fontFamilyOptions[0].value }}>
      <div>
        <TemplateRenderer id={key} part={part} options={options} />
      </div>
    </div>
  );
};

/**
 * カスタムブロックを描画する
 */
const blockRenderer = (
  contentBlock: ContentBlock,
  options: EditorDisplayOptions,
) => {
  if (contentBlock.getType() !== "atomic") {
    return {
      component: () => <></>,
      editable: false,
    };
  }

  const part = contentBlock.getData().get(TEMPLATE_PART_BLOCK_KEY) as
    | (TemplatePart & TemplatePartWithContent)
    | undefined;

  if (!part) {
    return;
  }

  return {
    component: CustomBlockComponent,
    editable: false,
    props: {
      part,
      options,
    },
  };
};

export interface EditorDisplayOptions {
  partsProps: {
    ctaButtonService?: {
      formPath?: string;
      confirmPath?: string;
    };
    serviceViewType?: ServiceViewType;
  };
}

const defaultOptions: EditorDisplayOptions = {
  partsProps: {
    ctaButtonService: {
      formPath: "",
      confirmPath: "",
    },
  },
};

interface Props {
  state: EditorState;
  options?: EditorDisplayOptions;
}

export default function EditorIframeDisplay({
  state,
  options,
  ...other
}: Props) {
  const [editorState, setEditorState] = useState(state);
  const mergedOptions = options === undefined ? defaultOptions : options;
  const editorRef = useRef<Editor>(null);
  const onChange = useCallback(() => {
    // 何もしない
  }, []);

  // blockRendererFnが再描画されないため強制的に更新する
  useEffect(() => {
    setEditorState(
      EditorState.forceSelection(editorState, editorState.getSelection()),
    );
  }, [options]);

  return (
    <>
      <DraftStyles className="rich-text-editor">
        <div {...other} className="leading-[1.5rem]">
          <Editor
            ref={editorRef}
            editorState={editorState}
            onChange={onChange}
            blockRendererFn={(component) =>
              blockRenderer(component, mergedOptions)
            }
            blockStyleFn={blockStyleFn}
            customStyleMap={customStyleMap}
            customStyleFn={customStyleFn}
            readOnly={true}
          />
        </div>
      </DraftStyles>
    </>
  );
}

interface TemplateRendererProps {
  id: string;
  part: TemplatePart;
  options: EditorDisplayOptions;
}

function TemplateRenderer({ part, options }: TemplateRendererProps) {
  const service = useServiceInfo();

  if (!service) return <></>;
  switch (part.type) {
    case "menu.calendar":
      return <ServiceMenuCalendarTemplate part={part} />;
    case "menu.eventSeminar":
    case "menu.form":
    case "menu.subscription":
      return (
        <>
          <ServiceMenuTemplate part={part as IServiceMenuTemplate} />
          {options.partsProps?.serviceViewType === ServiceViewType.FORM && (
            <>
              <ServiceHeadingTemplate part={part as HeadingTypeTemplate} />
              <ServiceFormTemplate part={part as FormServiceTemplate} />
            </>
          )}
          <CtaButtonTemplate
            part={part as CtaButtonTypeTemplate}
            formPath={options.partsProps.ctaButtonService?.formPath}
            confirmPath={options.partsProps.ctaButtonService?.confirmPath}
          />
        </>
      );
    case "menu.other":
      return (
        <>
          <ServiceFormTemplate part={part as FormServiceTemplate} />
          <CtaButtonTemplate
            part={part as CtaButtonTypeTemplate}
            confirmPath={options.partsProps?.ctaButtonService?.confirmPath}
          />
        </>
      );
    default:
      return <></>;
  }
}

/**
 * ブロック要素のスタイル(クラス名)を返却
 * @param block
 */
function blockStyleFn(block: ContentBlock): string {
  const classes: string[] = [];
  block.getData().forEach((v: string, k: string) => {
    switch (k) {
      case "text-align":
      case "float":
        classes.push(`${k}-${v}`);
        break;
      default:
        return null;
    }
  });
  return classes.length ? classes.join(" ") : "";
}
